// `?spike=1` Phaser 4 capability probe (M0 acceptance). Each check calls the phaserCompat wrapper the
// production renderer will use and verifies the effect (read-back values and, where cheap, canvas pixels).
import type { ViewportInfo } from '../../platform/viewport.ts';
import { emptySpikeReport, type SpikeCheck, type SpikeReport } from '../Renderer.ts';
import {
  addBurstEmitter,
  addCanvasTexture,
  addFrame,
  addImage,
  aliveParticles,
  alphaOf,
  backingSize,
  cameraState,
  crop,
  destroyObject,
  explode,
  frameSize,
  gameCanvas,
  glInfo,
  hasTexture,
  imageFrameName,
  isCropped,
  isWebGL,
  type PCanvasTexture,
  type PGame,
  type PImage,
  type PScene,
  placeImage,
  type Rgba,
  rendererMipmapFilter,
  resizeGame,
  scaleZoom,
  screenToWorld,
  setCamera,
  setOriginTopLeft,
  snapshotPixel,
  textureMipmapInfo,
  tintFill,
  tintState,
  tweenTo,
} from './phaserCompat.ts';
import { drawProbeSheet, SPIKE_PROBE_KEY } from './spikeArt.ts';

const PROBE_DEPTH = 1000;
/** Pixel read-backs per check before it reports what it saw (each waits for the next render). */
const PIXEL_TRIES = 6;
/** Probe-only particle settings (a capability check, not game juice: M10 reads juice.json). */
const PROBE_BURST = { lifespanMs: 600, speedMin: 60, speedMax: 240, scaleStart: 0.25, scaleEnd: 0 };
const near = (a: number, b: number, eps: number): boolean => Math.abs(a - b) <= eps;
const fmt = (p: Rgba | null): string => (p ? `${p.r},${p.g},${p.b}` : 'n/a');
const isWhite = (p: Rgba | null): boolean => p !== null && p.r >= 220 && p.g >= 220 && p.b >= 220;
const isRed = (p: Rgba | null): boolean => p !== null && p.r >= 200 && p.g <= 60 && p.b <= 60;
const isGrey = (p: Rgba | null): boolean =>
  p !== null && [p.r, p.g, p.b].every((v) => v >= 60 && v <= 195) && Math.abs(p.r - p.g) <= 24;
const isBW = (p: Rgba | null): boolean =>
  p !== null && [p.r, p.g, p.b].every((v) => v <= 40) !== [p.r, p.g, p.b].every((v) => v >= 215);

async function guard(fn: () => Promise<SpikeCheck> | SpikeCheck): Promise<SpikeCheck> {
  try {
    return await fn();
  } catch (err) {
    return { ok: false, note: `threw: ${err instanceof Error ? err.message : String(err)}` };
  }
}

function timeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p, new Promise<T>((r) => setTimeout(() => r(fallback), ms))]);
}

export async function runSpikeProbe(
  game: PGame,
  scene: PScene,
  vp: ViewportInfo,
  restoreCamera: () => void,
): Promise<SpikeReport> {
  const t0 = performance.now();
  const report = emptySpikeReport('phaser', vp, 'not run');
  const made: PImage[] = [];
  let tex: PCanvasTexture | null = null;
  const img = (x: number, y: number, frame: string, w: number, h: number): PImage => {
    if (!tex) throw new Error('no probe texture');
    const i = addImage(scene, 0, 0, SPIKE_PROBE_KEY, frame);
    setOriginTopLeft(i);
    placeImage(i, x, y, w, h, PROBE_DEPTH);
    made.push(i);
    return i;
  };
  const px = (x: number, y: number): Promise<Rgba | null> =>
    timeout(snapshotPixel(game, x, y), 1500, null);
  /** Re-reads (one render apart) until `settled` holds or the tries run out; returns the last read. A frame
   *  rendered before the probe's object change reached the GPU must not fail a capability check. */
  const pxUntil = async (
    x: number,
    y: number,
    settled: (p: Rgba | null) => boolean,
    tries = PIXEL_TRIES,
  ): Promise<Rgba | null> => {
    let p: Rgba | null = null;
    for (let i = 0; i < tries; i++) {
      p = await px(x, y);
      if (settled(p)) break;
    }
    return p;
  };

  try {
    report.checks.webgl = await guard(() => {
      const ok = isWebGL(game);
      const info = glInfo(game);
      report.gl = info;
      return { ok, note: info ? `${info.version} | ${info.renderer}` : 'no GL context' };
    });
    report.webgl = report.checks.webgl.ok;

    report.checks.cameraZoomScroll = await guard(() => {
      setCamera(scene, 2, 10, 20);
      const s = cameraState(scene);
      const w0 = screenToWorld(scene, 0, 0);
      const w1 = screenToWorld(scene, 100, 100);
      const ok =
        s.zoom === 2 &&
        s.scrollX === 10 &&
        s.scrollY === 20 &&
        near(w0.x, 10, 0.01) &&
        near(w0.y, 20, 0.01) &&
        near(w1.x, 60, 0.01) &&
        near(w1.y, 70, 0.01);
      // Probe objects below are placed in backing-store px: identity camera.
      setCamera(scene, 1, 0, 0);
      return {
        ok,
        note: `zoom=${s.zoom} scroll=${s.scrollX},${s.scrollY} w(100,100)=${w1.x},${w1.y}`,
      };
    });

    report.checks.addCanvasFrames = await guard(() => {
      tex = hasTexture(scene, SPIKE_PROBE_KEY)
        ? null
        : addCanvasTexture(scene, SPIKE_PROBE_KEY, drawProbeSheet());
      if (!tex) return { ok: false, note: 'addCanvas returned null' };
      const names = ['q0', 'q1', 'q2', 'q3'];
      const added = names.map((n, i) =>
        addFrame(tex as PCanvasTexture, n, (i % 2) * 128, Math.floor(i / 2) * 128, 128, 128),
      );
      const sizes = names.map((n) => frameSize(tex as PCanvasTexture, n));
      const ok =
        added.every(Boolean) && sizes.every((s) => s !== null && s.w === 128 && s.h === 128);
      return { ok, note: `frames=${added.filter(Boolean).length}/4` };
    });

    // image: white q0 at (8,8) 16×16 → pixel white
    const white = img(8, 8, 'q0', 16, 16);
    report.checks.image = await guard(async () => {
      const p = await pxUntil(16, 16, isWhite);
      const ok = imageFrameName(white) === 'q0' && isWhite(p);
      return { ok, note: `frame=${imageFrameName(white)} px=${fmt(p)}` };
    });

    // tint: white q0 at (32,8) filled red → pixel red
    const tinted = img(32, 8, 'q0', 16, 16);
    report.checks.tint = await guard(async () => {
      tintFill(tinted, 0xff0000);
      const st = tintState(tinted);
      const p = await pxUntil(40, 16, isRed);
      const ok = st.fill && st.color === 0xff0000 && isRed(p);
      return { ok, note: `tint=${st.color.toString(16)} fill=${st.fill} px=${fmt(p)}` };
    });

    // setCrop: red q1 at (56,8) shown 32×32, cropped to the top-left quarter → inside red, outside not red
    const cropped = img(56, 8, 'q1', 32, 32);
    report.checks.setCrop = await guard(async () => {
      crop(cropped, 0, 0, 64, 64);
      const inside = await pxUntil(60, 12, isRed);
      const outside = await pxUntil(84, 36, (p) => p !== null && !isRed(p));
      const ok = isCropped(cropped) && isRed(inside) && !isRed(outside);
      return { ok, note: `inside=${fmt(inside)} outside=${fmt(outside)}` };
    });

    // mipmaps: 1-px checker q3 at 1/31 scale; texel-centre samples are pure B/W without mips, grey with mips
    const size = 128 / 31;
    img(100, 8, 'q3', size, size);
    report.checks.mipmaps = await guard(async () => {
      const filter = rendererMipmapFilter(game);
      const info = textureMipmapInfo(tex as PCanvasTexture);
      // settled = a decisive read: grey (mipmapped) or pure black/white (aliased), not the wall colour
      const decisive = (p: Rgba | null): boolean => isGrey(p) || isBW(p);
      const a = await pxUntil(100, 8, decisive);
      const b = await pxUntil(101, 8, decisive);
      const grey = isGrey(a) && isGrey(b);
      const aliased = isBW(a) || isBW(b);
      const ok = filter !== 0 && info.isMipmap && info.pot && grey;
      return {
        ok,
        note: `filter=${filter} isMipmap=${info.isMipmap} pot=${info.pot} px=${fmt(a)}/${fmt(b)}${aliased ? ' (aliased)' : ''}`,
      };
    });
    report.mipmaps = report.checks.mipmaps.ok;

    report.checks.tween = await guard(async () => {
      const done = await timeout(
        tweenTo(scene, white, { alpha: 0.5 }, 60).then(() => true),
        2000,
        false,
      );
      const a = alphaOf(white);
      return { ok: done && near(a, 0.5, 0.01), note: `completed=${done} alpha=${a.toFixed(3)}` };
    });

    report.checks.particlesExplode = await guard(() => {
      const e = addBurstEmitter(scene, SPIKE_PROBE_KEY, 'q0', PROBE_DEPTH, PROBE_BURST);
      explode(e, 16, 200, 40);
      const n = aliveParticles(e);
      setTimeout(() => destroyObject(e), 800);
      return { ok: n === 16, note: `alive=${n}` };
    });

    report.checks.scaleResizeZoom = await guard(() => {
      const c = gameCanvas(game);
      const cssW = vp.iw;
      const cssH = vp.ih;
      const small = backingSize(cssW - 10, cssH - 10, vp.dprEff);
      resizeGame(game, cssW - 10, cssH - 10, vp.dprEff);
      const changed =
        c.width === small.w && c.height === small.h && c.style.width === `${cssW - 10}px`;
      resizeGame(game, cssW, cssH, vp.dprEff);
      const full = backingSize(cssW, cssH, vp.dprEff);
      const restored = c.width === full.w && c.height === full.h && c.style.width === `${cssW}px`;
      const z = scaleZoom(game);
      const ok = changed && restored && near(z, 1 / vp.dprEff, 1e-6);
      return {
        ok,
        note: `canvas=${c.width}x${c.height} css=${c.style.width} zoom=${z.toFixed(4)}`,
      };
    });
  } finally {
    for (const i of made) destroyObject(i);
    restoreCamera();
  }
  report.ms = Math.round(performance.now() - t0);
  return report;
}
